import React from 'react';
import { Box, Divider, Flex, TextV2 } from '@withjoy/joykit';
import { Coordinates, AccommodationListingDTO } from '@apps/accommodations/AccommodationsConsole.types';
import { AccommodationsConsoleState } from '@apps/accommodations/AccommodationsConsole.types';
import { useAccommodationRedirectUrl } from '@apps/accommodations/hooks/useAccommodationRedirectUrl';

import { ListingPhotos } from './ListingPhotos';
import { HeaderBlock } from './HeaderBlock';
import { TravelLogistics } from './TravelLogistics';
import { CostBreakdown } from './CostBreakdown';
import { HotelCTA } from './HotelCTA';
import { CouplesPick } from './CouplesPick';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { useTranslation } from '@shared/core';
import { format } from 'date-fns';
import { UserNote } from './UserNote';
import { useListingsPanelTelemetry } from '../ListingsPanel.telemetry';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useAccommodationsConsoleConfig } from '@apps/accommodations/AccommodationsConsoleConfigContext';
import { SoldOutMessage } from './SoldOutMessage';

const Container = ({ children, id }: { children: React.ReactNode; id?: string }) => {
  return (
    <Flex
      id={id}
      flexDirection={{ _: 'column', md: 'row' }}
      columnGap={4}
    >
      {children}
    </Flex>
  );
};

const ContentPanel = ({ children }: { children: React.ReactNode }) => {
  return (
    <Flex
      flexDirection="column"
      width={'100%'}
      gap={{ _: 4, md: 0 }}
      paddingY={{ _: 4, md: 0 }}
    >
      {children}
    </Flex>
  );
};

const LogisticsAndCostBlock = ({ children }: { children: React.ReactNode }) => {
  return (
    <Flex
      flexDirection={{ _: 'column', md: 'row' }}
      gap={{ _: 4, md: 0 }}
      marginTop={{ _: 0, md: 6 }}
      justifyContent={{ _: 'flex-start', md: 'space-between' }}
      alignItems={{ _: 'none', md: 'center' }}
      width={{ _: '100%', md: 'auto' }}
    >
      {children}
    </Flex>
  );
};

const CutoffDate = ({ cutoffDate }: { cutoffDate: string }) => {
  const { t } = useTranslation('accommodationsConsole');
  const listingsPanelTrans = t('listingsPanel');
  const formattedCutoffDate = format(new Date(cutoffDate), 'MMMM do, yyyy');

  return (
    <Flex justifyContent={'flex-end'}>
      <TextV2
        typographyVariant="label1"
        color={'mono10'}
      >
        {listingsPanelTrans.cutoffDate({ cutoffDate: formattedCutoffDate })}
      </TextV2>
    </Flex>
  );
};

const ListingEntry = (props: { venueCoordinates: Coordinates | null; listing: AccommodationListingDTO; state: AccommodationsConsoleState; noOfNights: number }) => {
  const { listing, venueCoordinates, state, noOfNights } = props;
  const isMobileOrTablet = useIsMobileOrTablet();
  const getRedirectUrl = useAccommodationRedirectUrl(state);
  const config = useAccommodationsConsoleConfig();
  const redirectUrl = getRedirectUrl(listing);
  const telemetry = useListingsPanelTelemetry();
  const handleSeeAvailability = useEventCallback(() => {
    telemetry.seeAvailabilityClicked({ joyPlaceId: listing.joyPlaceId || '', hotelName: listing.name });
  });

  return (
    <Container id={`listing-${listing.joyPlaceId}`}>
      <ListingPhotos
        listing={listing}
        isMobileOrTablet={isMobileOrTablet}
      />

      <Box
        as="a"
        width="100%"
        href={redirectUrl}
        onClick={handleSeeAvailability}
        target="_blank"
      >
        <Flex
          flexDirection="column"
          width={'100%'}
          height={'100%'}
          justifyContent={'space-between'}
        >
          <ContentPanel>
            {listing.isPickedByCouple && <CouplesPick listing={listing} />}
            <HeaderBlock
              listing={listing}
              venueCoordinates={venueCoordinates}
              isMobileOrTablet={isMobileOrTablet}
            />
            {listing.type === 'roomBlock' && listing.userNote && config?.listingsPanel?.listingEntry?.shouldShowRoomBlockNoteAndDate && <UserNote note={listing.userNote} />}
            {isMobileOrTablet && <Divider marginY={3} />}
            <LogisticsAndCostBlock>
              {!isMobileOrTablet && (
                <TravelLogistics
                  venueCoordinates={venueCoordinates}
                  listing={listing}
                />
              )}
              <Flex
                flexDirection={'column'}
                gap={5}
              >
                {listing.type === 'roomBlock' && config?.listingsPanel?.listingEntry?.shouldShowRoomBlockNoteAndDate && <CutoffDate cutoffDate={listing.cutoffDate} />}
                {listing.pricingInfo.isLoading ? (
                  <CostBreakdown.Skeleton />
                ) : listing.pricingInfo.values ? (
                  <CostBreakdown
                    occupancies={state.occupancies}
                    pricingInfo={listing.pricingInfo.values}
                    noOfNights={noOfNights}
                  />
                ) : null}
              </Flex>
            </LogisticsAndCostBlock>
            {listing.type === 'standardHotel' && listing.isSoldOutForSelectedDates && <SoldOutMessage />}
          </ContentPanel>
          {(listing.type === 'standardHotel' && !listing.isSoldOutForSelectedDates) || listing.type === 'roomBlock' ? (
            <Flex
              justifyContent={'flex-end'}
              width={'100%'}
            >
              <HotelCTA
                listing={listing}
                redirectUrl={redirectUrl}
              />
            </Flex>
          ) : null}
        </Flex>
      </Box>
    </Container>
  );
};

ListingEntry.Skeleton = ({ isMobileOrTablet }: { isMobileOrTablet: boolean }) => {
  return (
    <Container>
      <ListingPhotos.Skeleton />
      <ContentPanel>
        <HeaderBlock.Skeleton isMobileOrTablet={isMobileOrTablet} />
        {isMobileOrTablet && <Divider marginY={3} />}
        <LogisticsAndCostBlock>
          {!isMobileOrTablet && <TravelLogistics.Skeleton />}
          <CostBreakdown.Skeleton />
        </LogisticsAndCostBlock>
      </ContentPanel>
    </Container>
  );
};

export { ListingEntry };
